import { useEffect, useRef, useState } from "react";
import { Bot, LoaderCircle, MessageCircle, Send, Trash2, X } from "lucide-react";
import API, { extractApiError } from "../../services/api";
import { baseUrl } from "../../services/BaseURL";

const STORAGE_KEY = "prosale:ollama-chat";
const WELCOME = {
  role: "assistant",
  content: "Hi, I am LiMo. How can I help?",
};

export default function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-20)));
    } catch {
      // Session storage is optional.
    }
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, sending]);

  useEffect(() => {
    if (!open || status) return;
    API.get("/ai/status", { timeout: 10_000 })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ enabled: true, configured: false }));
  }, [open, status]);

  async function sendMessage(event) {
    event?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const userMessage = { role: "user", content: text };
    const history = messages
      .slice(1)
      .filter((message) => !message.error)
      .slice(-12)
      .map(({ role, content }) => ({ role, content }));

    setMessages((current) => [...current, userMessage]);
    setInput("");
    setSending(true);

    try {
      const { data } = await API.post(
        "/ai/chat",
        { message: text, history },
        { timeout: 130_000 }
      );
      setMessages((current) => [
        ...current,
        { role: "assistant", content: data.answer },
      ]);
      setStatus((current) => ({
        ...(current || {}),
        enabled: true,
        configured: true,
        model: data.model,
      }));
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          error: true,
          content: extractApiError(error, "The AI assistant is unavailable."),
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  function clearChat() {
    setMessages([WELCOME]);
    setInput("");
  }

  return (
    <>
      {open && (
        <section
          aria-label="AI assistant"
          className="fixed bottom-20 right-4 z-50 flex h-[min(560px,calc(100vh-7rem))] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        >
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4 dark:border-slate-700">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                <Bot size={18} />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  LiMo
                </h2>
                <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                  {status?.enabled && status?.configured
                    ? "Private AI assistant"
                    : status
                    ? status.enabled
                      ? "Ollama configuration required"
                      : "AI module disabled"
                    : "Checking connection..."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={clearChat}
                title="Clear conversation"
                className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <Trash2 size={16} />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                title="Close assistant"
                className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
          </header>

          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={[
                    "max-w-[86%] whitespace-pre-wrap break-words px-3 py-2 text-sm leading-5",
                    message.role === "user"
                      ? "rounded-lg rounded-br-sm bg-blue-600 text-white"
                      : message.error
                      ? "rounded-lg rounded-bl-sm bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
                      : "rounded-lg rounded-bl-sm bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-100",
                  ].join(" ")}
                >
                  <ChatMessageContent content={message.content} />
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-500 dark:bg-slate-800 dark:text-gray-300">
                  <LoaderCircle size={15} className="animate-spin" />
                  Thinking...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={sendMessage}
            className="flex shrink-0 items-end gap-2 border-t border-gray-200 p-3 dark:border-slate-700"
          >
            <textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              rows={1}
              maxLength={4000}
              placeholder="Ask LiMo..."
              className="max-h-28 min-h-10 flex-1 resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              title="Send message"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? (
                <LoaderCircle size={17} className="animate-spin" />
              ) : (
                <Send size={17} />
              )}
            </button>
          </form>
        </section>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        title={open ? "Close AI assistant" : "Open AI assistant"}
        aria-expanded={open}
        className="fixed bottom-5 right-5 z-50 grid h-12 w-12 place-items-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/25"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </>
  );
}

function ChatMessageContent({ content }) {
  const text = String(content || "");
  const urlPattern = /(https?:\/\/[^\s]+|\/uploads\/[^\s]+)/g;
  const parts = text.split(urlPattern);

  return (
    <div className="space-y-2">
      {parts.map((part, index) => {
        if (!part) return null;
        const clean = part.replace(/[),.;]+$/, "");
        const suffix = part.slice(clean.length);
        if (!/^(https?:\/\/|\/uploads\/)/.test(clean)) {
          return <span key={index}>{part}</span>;
        }

        const resolved = clean.startsWith("/uploads/") ? `${baseUrl}${clean}` : clean;
        const isImage = clean.startsWith("/uploads/")
          || /\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(clean);
        const isMap = /maps\.google\.com\/maps\?q=/i.test(clean);

        return (
          <span key={index} className="block">
            {isImage && (
              <a href={resolved} target="_blank" rel="noreferrer" className="block">
                <img
                  src={resolved}
                  alt="Attendance capture"
                  loading="lazy"
                  className="max-h-52 w-full rounded-md border border-gray-200 object-cover dark:border-slate-600"
                />
              </a>
            )}
            {isMap && (
              <iframe
                title={`Attendance location ${index + 1}`}
                src={resolved}
                loading="lazy"
                className="h-48 w-full rounded-md border border-gray-200 dark:border-slate-600"
              />
            )}
            {!isImage && !isMap && (
              <a
                href={resolved}
                target="_blank"
                rel="noreferrer"
                className="break-all text-blue-600 underline dark:text-blue-300"
              >
                {clean}
              </a>
            )}
            {suffix}
          </span>
        );
      })}
    </div>
  );
}

function loadMessages() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
    if (Array.isArray(saved) && saved.length) return saved;
  } catch {
    // Use the welcome message when stored data is invalid.
  }
  return [WELCOME];
}
